import {
    importProvidersFrom,
    inject,
    isDevMode,
    provideAppInitializer,
} from "@angular/core";
import {
    HttpClient,
    provideHttpClient,
    withInterceptors,
} from "@angular/common/http";
import { bootstrapApplication } from "@angular/platform-browser";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { provideRouter, withComponentInputBinding } from "@angular/router";
import { provideEffects } from "@ngrx/effects";
import { provideStore } from "@ngrx/store";
import { provideStoreDevtools } from "@ngrx/store-devtools";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";

import { AppComponent } from "./app/app.component";
import { appRoutes } from "./app/app.routes";
import { CallEffects } from "./app/features/calls/store/call.effects";
import { callFeature } from "./app/features/calls/store/call.reducer";
import { SessionEffects } from "./app/features/authentication/store/session.effects";
import { sessionFeature } from "./app/features/authentication/store/session.reducer";
import {
    APP_CONFIG,
    developmentConfig,
    productionConfig,
} from "./app/core/config/app-config";
import { authTokenInterceptor } from "./app/core/http/auth-token.interceptor";
import { AppInitializationService } from "./app/core/config/app-initialization.service";
import { RealtimeEffects } from "./app/core/realtime/store/realtime.effects";
import { realtimeFeature } from "./app/core/realtime/store/realtime.reducer";
import { SessionConflictEffects } from "./app/core/realtime/presentation/session-conflict.effects";
import { ConnectionRecoveryEffects } from "./app/core/realtime/presentation/connection-recovery.effects";
import { NotificationEffects } from "./app/features/notifications/store/notification.effects";
import { notificationFeature } from "./app/features/notifications/store/notification.reducer";
import { conferenceFeature } from "./app/features/multiconference/store/conference.reducer";
import { ConferenceEffects } from "./app/features/multiconference/store/conference.effects";
import { contactsFeature } from "./app/features/contacts/store/contacts.reducer";
import { ContactsEffects } from "./app/features/contacts/store/contacts.effects";
import { profileFeature } from "./app/features/profile/store/profile.reducer";
import { ProfileEffects } from "./app/features/profile/store/profile.effects";
import { companyEntitlementFeature } from "./app/features/billing/store/company-entitlement.reducer";
import { CompanyEntitlementEffects } from "./app/features/billing/store/company-entitlement.effects";
import { subscriptionsFeature } from "./app/features/billing/store/subscriptions.reducer";
import { SubscriptionsEffects } from "./app/features/billing/store/subscriptions.effects";
import { shopFeature } from "./app/features/billing/store/shop.reducer";
import { ShopEffects } from "./app/features/billing/store/shop.effects";
import { usersFeature } from "./app/features/users/store/users.reducer";
import { UsersEffects } from "./app/features/users/store/users.effects";
import { actionsFeature } from "./app/features/actions/store/actions.reducer";
import { TaskActionEffects } from "./app/features/actions/store/actions.effects";
import { groupsFeature } from "./app/features/groups/store/groups.reducer";
import { GroupsEffects } from "./app/features/groups/store/groups.effects";
import { workflowsFeature } from "./app/features/workflows/store/workflows.reducer";
import { WorkflowsEffects } from "./app/features/workflows/store/workflows.effects";
import { formsFeature } from "./app/features/forms/store/forms.reducer";
import { FormsEffects } from "./app/features/forms/store/forms.effects";
import { reportsFeature } from "./app/features/reports/store/reports.reducer";
import { ReportsEffects } from "./app/features/reports/store/reports.effects";
import { knowledgeFeature } from "./app/features/knowledge-base/store/knowledge.reducer";
import { KnowledgeEffects } from "./app/features/knowledge-base/store/knowledge.effects";
import { invitesFeature } from "./app/features/invites/store/invites.reducer";
import { InvitesEffects } from "./app/features/invites/store/invites.effects";

bootstrapApplication(AppComponent, {
    providers: [
        provideRouter(appRoutes, withComponentInputBinding()),
        provideAnimationsAsync(),
        provideHttpClient(withInterceptors([authTokenInterceptor])),
        importProvidersFrom(
            TranslateModule.forRoot({
                defaultLanguage: "en",
                loader: {
                    provide: TranslateLoader,
                    useFactory: (http: HttpClient) =>
                        new TranslateHttpLoader(http),
                    deps: [HttpClient],
                },
            })
        ),
        {
            provide: APP_CONFIG,
            useValue: isDevMode() ? developmentConfig : productionConfig,
        },
        provideStore({
            [callFeature.name]: callFeature.reducer,
            [sessionFeature.name]: sessionFeature.reducer,
            [realtimeFeature.name]: realtimeFeature.reducer,
            [notificationFeature.name]: notificationFeature.reducer,
            [conferenceFeature.name]: conferenceFeature.reducer,
            [contactsFeature.name]: contactsFeature.reducer,
            [profileFeature.name]: profileFeature.reducer,
            [companyEntitlementFeature.name]: companyEntitlementFeature.reducer,
            [subscriptionsFeature.name]: subscriptionsFeature.reducer,
            [shopFeature.name]: shopFeature.reducer,
            [usersFeature.name]: usersFeature.reducer,
            [actionsFeature.name]: actionsFeature.reducer,
            [groupsFeature.name]: groupsFeature.reducer,
            [workflowsFeature.name]: workflowsFeature.reducer,
            [formsFeature.name]: formsFeature.reducer,
            [reportsFeature.name]: reportsFeature.reducer,
            [knowledgeFeature.name]: knowledgeFeature.reducer,
            [invitesFeature.name]: invitesFeature.reducer,
        }),
        provideEffects(
            CallEffects,
            SessionEffects,
            RealtimeEffects,
            SessionConflictEffects,
            ConnectionRecoveryEffects,
            NotificationEffects,
            ConferenceEffects,
            ContactsEffects,
            ProfileEffects,
            CompanyEntitlementEffects,
            SubscriptionsEffects,
            ShopEffects,
            TaskActionEffects,
            UsersEffects,
            GroupsEffects,
            WorkflowsEffects,
            FormsEffects,
            ReportsEffects,
            KnowledgeEffects,
            InvitesEffects
        ),
        provideAppInitializer(() =>
            inject(AppInitializationService).initialize()
        ),
        provideStoreDevtools({
            name: "Gemvision Next",
            maxAge: 40,
            logOnly: !isDevMode(),
            connectInZone: true,
            trace: isDevMode(),
            traceLimit: 25,
        }),
    ],
}).catch((error: unknown) => console.error(error));
